/**
 * The member's side panel, one for the whole account area.
 *
 * The dashboard, every list its rows open (orders, bids, deals, notifications,
 * documents, transactions, bookmarks, requests, claims) and the settings share
 * this one rail, mounted once by the layout route, so moving between them never
 * changes it. A page's own filters live in the page, never in the rail.
 *
 * A row carries a number only when something behind it waits on the member:
 * unread notifications and open requests. Totals (how many deals, documents or
 * bookmarks exist) are the list's own business, not the rail's.
 */

import { Suspense, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ROUTES } from '@metal/shared'
import { Rail, Skeleton, type RailGroup, type RailRow } from '@metal/ui'
import { api } from '@metal/ui/api'
import { useSession } from '@metal/ui/session'
import { qk } from '@/lib/queries'
import { PERMISSION } from '@/lib/access'
import { approvalKeys, type ApprovalList } from '@/lib/approvals'
import { ticketsKey, type TicketsResponse } from '@/lib/tickets'

/** The part of the requests list the rail reads: the per-tab counts. */
interface RequestsHead {
  tabs: Array<{ key: string; count: number }>
}

/**
 * Unread notifications, read under the notifications page's own summary key,
 * so marking them read there clears the rail number and the bell at once.
 * Until the read lands, the count the session carried is used.
 *
 * A notice arrives without the member doing anything, so the count is read
 * again every minute and whenever the tab is returned to: a case result
 * announced while the page stood open reaches the bell without a reload.
 */
export function useUnreadNotifications(): number | undefined {
  const { user } = useSession()
  const inbox = useQuery({
    queryKey: qk.notifications({ view: 'summary' }),
    queryFn: () => api.get<{ unread: number }>('/notifications/summary'),
    enabled: Boolean(user),
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
  return inbox.data?.unread ?? user?.unreadNotifications
}

/** Which row the current path belongs to. */
function activeOf(path: string): string | undefined {
  const rules: Array<[RegExp, string]> = [
    [/^\/dashboard/, 'overview'],
    [/^\/offers\/mine/, 'offers'],
    [/^\/rfqs\/mine/, 'rfqs'],
    [/^\/bids/, 'bids'],
    [/^\/deals/, 'deals'],
    [/^\/notifications/, 'notifications'],
    [/^\/documents/, 'documents'],
    [/^\/wallet/, 'transactions'],
    [/^\/favorites/, 'favorites'],
    [/^\/claims/, 'claims'],
    [/^\/requests/, 'requests'],
    [/^\/approvals/, 'approvals'],
    [/^\/tickets/, 'tickets'],
    [/^\/account\/grade/, 'grade'],
    [/^\/ads\/(mine|new|[^/]+\/edit)/, 'ads'],
    [/^\/settings\/profile/, 'settings-profile'],
    [/^\/settings\/users/, 'settings-users'],
    [/^\/settings\/addresses/, 'settings-addresses'],
    [/^\/settings\/kyc/, 'settings-kyc'],
    [/^\/settings\/notifications/, 'settings-notifications'],
    [/^\/settings\/security/, 'settings-security'],
  ]
  return rules.find(([re]) => re.test(path))?.[1]
}

export function AccountRail() {
  const { pathname } = useLocation()
  const { user, can } = useSession()
  const trading = user?.membership === 'active'
  const company = user?.partyKind === 'company'
  const sells = Boolean(user?.sides.includes('sell'))
  const buys = Boolean(user?.sides.includes('buy'))

  // Open requests, read as the first page of the requests list's open tab under
  // that page's own key: the rail and the list share one read and one answer.
  const requests = useQuery({
    queryKey: qk.requests({ tab: 'open', page: 1 }),
    queryFn: () => api.get<RequestsHead>('/requests', { tab: 'open', page: 1 }),
    enabled: trading,
    staleTime: 60_000,
  })
  const openRequests = requests.data?.tabs.find((t) => t.key === 'open')?.count

  // Open claims, read under the claims list's own first page key, so the rail
  // and the list share one read.
  const claims = useQuery({
    queryKey: ['claims', 'mine', { tab: 'open', page: 1 }],
    queryFn: () => api.get<{ total: number }>('/claims/mine', { tab: 'open', page: 1 }),
    enabled: trading,
    staleTime: 60_000,
  })
  const openClaims = claims.data?.total || undefined

  // Tickets answered by support wait on the member, so those are counted. Read
  // under the tickets list's own first-page key, so both share one answer.
  const tickets = useQuery({
    queryKey: ticketsKey({ status: 'all', page: 1 }),
    queryFn: () => api.get<TicketsResponse>('/tickets', { status: 'all', page: 1 }),
    enabled: Boolean(user),
    staleTime: 60_000,
  })
  const answeredTickets = tickets.data?.counts.answered || undefined

  // Operations a company approval policy parked that this person may decide.
  // Read under the queue's own pending first-page key shape, so a decision
  // there refreshes this number too.
  const approvals = useQuery({
    queryKey: approvalKeys.list({ state: 'PENDING', mine: 'approver', page: 1 }),
    queryFn: () => api.get<ApprovalList>('/org/approval-requests', { state: 'PENDING', mine: 'approver', page: 1 }),
    enabled: trading && company,
    staleTime: 60_000,
    refetchInterval: 120_000,
  })
  const awaitingApproval = approvals.data?.total || undefined

  const unread = useUnreadNotifications()

  // A file not trading yet opens only the overview, its notifications and its settings.
  // A suspended member still reads everything but its own listings, which the guard sends home.
  const suspended = user?.membership === 'suspended'
  const locked = !trading && !suspended
  const row = (r: RailRow): RailRow => (locked ? { ...r, disabled: true } : r)
  const listingRow = (r: RailRow): RailRow => (locked || suspended ? { ...r, disabled: true } : r)

  const orderRows: RailRow[] = []
  if (sells || (!sells && !buys)) {
    orderRows.push(
      listingRow({ key: 'offers', to: ROUTES.myOffers(), label: 'عرضه‌ها', icon: 'cathode' }),
    )
  }
  if (buys) {
    orderRows.push(
      listingRow({ key: 'rfqs', to: ROUTES.myRfqs(), label: 'تقاضاها', icon: 'list' }),
    )
  }

  // On a phone the rail is one scrolling strip: the open page's chip is
  // brought into view, never the page scrolled for it.
  useEffect(() => {
    const strip = document.querySelector<HTMLElement>('.account-rail')
    const on = strip?.querySelector<HTMLElement>('a.is-on')
    if (!strip || !on || strip.scrollWidth <= strip.clientWidth) return
    strip.scrollLeft = on.offsetLeft - (strip.clientWidth - on.offsetWidth) / 2
  }, [pathname])

  const inSettings = pathname.startsWith('/settings')
  const profileLabel = user?.partyKind === 'person' ? 'حساب کاربری' : 'اطلاعات شرکت'

  const groups: RailGroup[] = [
    { rows: [{ key: 'overview', to: ROUTES.dashboard(), label: 'نمای کلی', icon: 'grid' }] },
    {
      heading: 'بخش‌های داشبورد',
      rows: [
        ...orderRows,
        row({ key: 'bids', to: ROUTES.bids(), label: 'پیشنهادها', icon: 'gavel' }),
        row({ key: 'deals', to: ROUTES.deals(), label: 'معاملات', icon: 'exchange' }),
        {
          key: 'notifications',
          to: ROUTES.notifications(),
          label: 'اعلان‌ها',
          icon: 'bell',
          count: unread,
        },
        row({ key: 'documents', to: ROUTES.documents(), label: 'مدارک', icon: 'doc' }),
        row({ key: 'transactions', to: ROUTES.walletTransactions(), label: 'گردش حساب', icon: 'wallet' }),
        row({ key: 'favorites', to: ROUTES.favorites(), label: 'نشان‌شده‌ها', icon: 'star' }),
        row({ key: 'requests', to: ROUTES.requests(), label: 'درخواست‌ها', icon: 'flag', count: openRequests }),
        row({ key: 'claims', to: ROUTES.claims(), label: 'شکایت‌ها', icon: 'scale', count: openClaims }),
        // A company's parked operations; only a company has an approval policy.
        ...(company
          ? [row({ key: 'approvals', to: ROUTES.approvals(), label: 'تأیید درخواست‌ها', icon: 'check', count: awaitingApproval })]
          : []),
        // Open before trading too: a correction to the file is asked here.
        { key: 'tickets', to: ROUTES.tickets(), label: 'تیکت‌ها', icon: 'message', count: answeredTickets },
        // Open before trading too: an advert is not a trade.
        { key: 'ads', to: ROUTES.myAds(), label: 'آگهی‌ها', icon: 'edit' },
      ],
    },
    inSettings
      ? {
          heading: 'تنظیمات حساب',
          rows: [
            { key: 'settings-profile', to: ROUTES.settingsProfile(), label: profileLabel, icon: 'building' },
            // A natural person has no sub users or roles; only a company does.
            // A colleague without «کاربران» reads their own access on the same page.
            ...(company
              ? [
                  {
                    key: 'settings-users',
                    to: ROUTES.settingsUsers(),
                    label: can(PERMISSION.members) ? 'کاربران و نقش‌ها' : 'دسترسی من',
                    icon: 'users' as const,
                  },
                ]
              : []),
            {
              key: 'settings-addresses',
              to: ROUTES.settingsAddresses(),
              label: 'آدرس‌های تحویل',
              icon: 'truck',
            },
            { key: 'settings-kyc', to: ROUTES.settingsKyc(), label: 'احراز هویت', icon: 'shield' },
            { key: 'settings-notifications', to: ROUTES.settingsNotifications(), label: 'تنظیمات اعلان‌ها', icon: 'bell' },
            { key: 'settings-security', to: ROUTES.settingsSecurity(), label: 'امنیت و ورود', icon: 'lock' },
          ],
        }
      : {
          rows: [{ key: 'settings', to: ROUTES.settingsProfile(), label: 'تنظیمات', icon: 'settings' }],
        },
  ]

  return <Rail className="account-rail" groups={groups} activeKey={activeOf(pathname)} />
}

/** The layout route: the rail, and beside it whichever account page is open. */
export function AccountArea() {
  return (
    <>
      <AccountRail />
      {/* The rail stays drawn while the next page loads. */}
      <Suspense
        fallback={
          <div className="page">
            <Skeleton h={48} />
            <Skeleton h={240} />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </>
  )
}
